import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet as WalletIcon, TrendingUp, Clock, ArrowDown, ArrowUpRight, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { powerWallet, powerWalletHistory, powerWithdraw } from "@/lib/powerApi";

export default function PowerWallet() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const reload = async () => {
    try {
      const [ww, hh] = await Promise.all([powerWallet(), powerWalletHistory()]);
      setW(ww); setEntries(hh.entries || []);
    } catch (e) {
      if (e.response?.status === 401) nav("/login?next=/power/wallet");
      else toast.error("Couldn't load wallet");
    }
  };
  useEffect(() => { if (!loading && user) reload(); if (!loading && !user) nav("/login?next=/power/wallet"); /* eslint-disable-next-line */ }, [loading, user]);

  if (!w) return <div className="py-16 text-center text-[#0A1A2F]/50">Loading wallet…</div>;

  return (
    <div data-testid="power-wallet" className="pb-6 space-y-5">
      <div className="rounded-3xl bg-gradient-to-br from-[#1E88FF] to-[#0A1A2F] text-white p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-4">
            <WalletIcon className="w-3 h-3" /> Your Power Wallet
          </div>
          <div className="text-white/70 text-xs font-semibold uppercase tracking-widest">Current balance</div>
          <div data-testid="wallet-balance" className="text-4xl sm:text-6xl font-black tracking-tight mt-1">
            ${w.balance_usd.toFixed(2)}
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Stat label="Lifetime earnings" value={`$${w.lifetime_usd.toFixed(2)}`} icon={<TrendingUp className="w-4 h-4" />} testId="wallet-lifetime" />
            <Stat label="Pending" value={`$${w.pending_usd.toFixed(2)}`} icon={<Clock className="w-4 h-4" />} testId="wallet-pending" />
          </div>
          <button data-testid="wallet-withdraw-btn" onClick={() => setShowWithdraw(true)}
            disabled={w.balance_usd < w.min_withdraw_usd}
            className="mt-5 inline-flex items-center gap-2 bg-white text-[#0A1A2F] font-black px-6 py-3 rounded-full shadow-lg hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
            <ArrowDown className="w-4 h-4" /> Withdraw
          </button>
          {w.balance_usd < w.min_withdraw_usd && (
            <div className="text-white/70 text-[11px] mt-2">
              Minimum withdrawal is ${w.min_withdraw_usd.toFixed(2)} — keep earning to unlock.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4EAF3] shadow-sm">
        <div className="p-4 border-b border-[#E4EAF3] font-black text-[#0A1A2F]">Recent activity</div>
        {entries.length === 0 ? (
          <div data-testid="wallet-empty" className="p-8 text-center text-[#0A1A2F]/50 text-sm">
            No activity yet. Sign petitions and complete achievements to grow your balance.
          </div>
        ) : (
          <ul className="divide-y divide-[#E4EAF3]">
            {entries.map((e, i) => (
              <li key={i} data-testid={`wallet-entry-${i}`} className="flex items-center gap-3 p-4">
                <div className={"w-9 h-9 rounded-full flex items-center justify-center " + (e.type === "withdrawal" ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "bg-[#22C55E]/10 text-[#22C55E]")}>
                  {e.type === "withdrawal" ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDown className="w-4 h-4 rotate-180" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0A1A2F] truncate">{e.label}</div>
                  <div className="text-[11px] text-[#0A1A2F]/50">
                    {(e.created_at || "").slice(0, 10)}
                    {e.status && <> · <span className="uppercase">{e.status}</span></>}
                  </div>
                </div>
                {e.type === "withdrawal" ? (
                  <div className="font-black text-[#FF3B30]">
                    −${Math.abs(e.amount_usd).toFixed(2)}
                  </div>
                ) : (
                  <div className="mono text-[10px] uppercase tracking-widest text-[#22C55E] font-bold">Credited</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-[#F6F8FC] rounded-2xl p-4 flex items-start gap-3 text-xs text-[#0A1A2F]/70">
        <ShieldCheck className="w-4 h-4 text-[#1E88FF] mt-0.5 flex-shrink-0" />
        <div>
          Rewards recognize your overall participation on Jixxeo Power — not a direct share of ad revenue. Withdrawal limits, fraud checks and eligibility rules are set by admins. Balances update in real time.
        </div>
      </div>

      {showWithdraw && <WithdrawModal w={w} onClose={() => setShowWithdraw(false)} onDone={() => { setShowWithdraw(false); reload(); }} />}
    </div>
  );
}

function Stat({ label, value, icon, testId }) {
  return (
    <div data-testid={testId} className="bg-white/10 rounded-xl p-3 backdrop-blur">
      <div className="text-[10px] font-bold uppercase tracking-widest text-white/70 inline-flex items-center gap-1">{icon}{label}</div>
      <div className="text-xl font-black mt-0.5">{value}</div>
    </div>
  );
}

function WithdrawModal({ w, onClose, onDone }) {
  const [amount, setAmount] = useState(Math.min(w.balance_usd, Math.max(w.min_withdraw_usd, 10)).toFixed(2));
  const [method, setMethod] = useState("paypal");
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const payout = Math.max(0, Number(amount) - w.withdraw_fee_usd);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt < w.min_withdraw_usd) { toast.error(`Minimum is $${w.min_withdraw_usd.toFixed(2)}`); return; }
    if (amt > w.balance_usd) { toast.error("More than your balance"); return; }
    if (!handle.trim()) { toast.error("Enter your payout handle"); return; }
    setBusy(true);
    try {
      await powerWithdraw({ amount_usd: amt, method, handle });
      toast.success("Withdrawal requested");
      onDone?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't request withdrawal");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="withdraw-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#E4EAF3]">
          <div className="font-black text-xl text-[#0A1A2F]">Request withdrawal</div>
          <div className="text-xs text-[#0A1A2F]/60 mt-0.5">Available balance: <span className="font-bold text-[#0A1A2F]">${w.balance_usd.toFixed(2)}</span></div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A1A2F]/60 mb-1.5">Amount (USD)</div>
            <input data-testid="withdraw-amount" type="number" step="0.01" min={w.min_withdraw_usd} max={w.balance_usd} value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none font-black text-lg" />
            <div className="text-[11px] text-[#0A1A2F]/50 mt-1">Fee ${w.withdraw_fee_usd.toFixed(2)} · you'll receive <b>${payout.toFixed(2)}</b></div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A1A2F]/60 mb-1.5">Method</div>
            <div className="grid grid-cols-3 gap-2">
              {["paypal", "bank", "crypto"].map((m) => (
                <button key={m} type="button" data-testid={`withdraw-method-${m}`} onClick={() => setMethod(m)}
                  className={"py-2 rounded-xl font-semibold text-sm capitalize transition-colors " + (method === m ? "bg-[#1E88FF] text-white" : "bg-[#F6F8FC] border border-[#E4EAF3] text-[#0A1A2F]/70 hover:border-[#1E88FF]")}>
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A1A2F]/60 mb-1.5">
              {method === "paypal" ? "PayPal email" : method === "crypto" ? "Wallet address" : "Bank / IBAN"}
            </div>
            <input data-testid="withdraw-handle" value={handle} onChange={(e) => setHandle(e.target.value)}
              placeholder={method === "paypal" ? "you@paypal.com" : method === "crypto" ? "0x…" : "IBAN or routing/account"}
              className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
          </div>
          <button data-testid="withdraw-submit" type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-black py-3 rounded-full disabled:opacity-60">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request withdrawal"}
          </button>
        </div>
      </form>
    </div>
  );
}
